import { useMutation, useQuery } from "@apollo/client";
import { groupBy } from "lodash";
import moment from "moment";
import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  DeleteAskAiChatSessionMutationMutation,
  DeleteAskAiChatSessionMutationMutationVariables,
  GetAskAiChatSessionHistoryQueryQuery,
  UpdateAskAiChatSessionMutationMutation,
  UpdateAskAiChatSessionMutationMutationVariables,
} from "types/graphql-schema";

import deleteAskAiChatSessionMutation from "@apps/ask-ai/graphql/delete-ask-ai-chat-session-mutation";
import getAskAiChatSessionHistoryQuery from "@apps/ask-ai/graphql/get-ask-ai-chat-session-history-query";
import updateAskAiChatSessionMutation from "@apps/ask-ai/graphql/update-ask-ai-chat-session-mutation";
import { getChatSessionTitle } from "@apps/ask-ai/helpers";
import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

const NewChatIcon = getTopicflowIcon("pencilSquare");

const ChatHistoryList = ({
  onClickChatSessionId,
  className,
}: {
  onClickChatSessionId?: (chatSessionId: number | null) => void;
  className?: string;
}) => {
  const link = useLink();
  const label = useLabel();
  // The selected chat is reflected in the URL as /ask-ai/:chatSessionId (Home
  // rewrites the URL forward when a new chat is created), so we highlight the
  // chat matching the current path. Read from the location rather than
  // useParams so this works in the sidebar too, where the list renders outside
  // the /ask-ai route that owns the param.
  const location = useLocation();
  const activeChatSessionId = (() => {
    const match = location.pathname.match(/^\/ask-ai\/(\d+)/);
    return match ? parseInt(match[1]) : null;
  })();
  const { data, loading, fetchMore } =
    useQuery<GetAskAiChatSessionHistoryQueryQuery>(
      getAskAiChatSessionHistoryQuery,
    );
  const [deleteChatSession] = useMutation<
    DeleteAskAiChatSessionMutationMutation,
    DeleteAskAiChatSessionMutationMutationVariables
  >(deleteAskAiChatSessionMutation);
  const [updateChatSession] = useMutation<
    UpdateAskAiChatSessionMutationMutation,
    UpdateAskAiChatSessionMutationMutationVariables
  >(updateAskAiChatSessionMutation);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  // Guards against the rename being committed twice when Enter blurs the input.
  const finishingRenameRef = useRef(false);

  const chatSessions = data?.chatSessions
    ? assertEdgesNonNull(data.chatSessions)
    : [];
  type ChatNode = (typeof chatSessions)[number];

  const handleLoadMore = () => {
    if (data?.chatSessions?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.chatSessions?.pageInfo?.endCursor,
          merge: true,
        },
      });
    }
  };

  const handleDelete = (id: number) => {
    deleteChatSession({
      variables: { id },
      refetchQueries: [getAskAiChatSessionHistoryQuery],
      onCompleted: () => {
        if (window.location.pathname.includes(`/ask-ai/${id}`)) {
          link.redirect("/");
        }
      },
    });
  };

  const handleTogglePin = (chat: ChatNode) => {
    const pinned = !chat.pinnedAt;
    updateChatSession({
      variables: { id: chat.id, pinned },
      optimisticResponse: {
        __typename: "Mutation",
        updateChatSession: {
          __typename: "UpdateChatSessionMutation",
          chatSession: {
            __typename: "ChatSessionNode",
            id: chat.id,
            title: chat.title ?? null,
            pinnedAt: pinned ? new Date().toISOString() : null,
          },
        },
      },
      onError: () =>
        errorNotificationVar({
          title: "Error",
          description: "The chat could not be updated.",
        }),
    });
  };

  const startRename = (chat: ChatNode) => {
    finishingRenameRef.current = false;
    setEditingId(chat.id);
    setDraftTitle(chat.title ?? "");
  };

  const cancelRename = () => {
    finishingRenameRef.current = true;
    setEditingId(null);
    setDraftTitle("");
  };

  const saveRename = (chat: ChatNode) => {
    if (finishingRenameRef.current) return;
    finishingRenameRef.current = true;
    const trimmed = draftTitle.trim();
    setEditingId(null);
    setDraftTitle("");
    if (trimmed === (chat.title ?? "")) return;
    updateChatSession({
      variables: { id: chat.id, title: trimmed },
      optimisticResponse: {
        __typename: "Mutation",
        updateChatSession: {
          __typename: "UpdateChatSessionMutation",
          chatSession: {
            __typename: "ChatSessionNode",
            id: chat.id,
            title: trimmed || null,
            pinnedAt: chat.pinnedAt ?? null,
          },
        },
      },
      onError: () =>
        errorNotificationVar({
          title: "Error",
          description: "The chat could not be renamed.",
        }),
    });
  };

  const pinnedChats = chatSessions.filter((chat) => chat.pinnedAt);
  const unpinnedChats = chatSessions.filter((chat) => !chat.pinnedAt);

  const chatsWithPeriod = unpinnedChats.map((chatSession) => {
    const created = moment(chatSession.created);
    const period = created.isSame(moment(), "day")
      ? "Today"
      : created.isSame(moment().subtract(1, "day"), "day")
        ? "Yesterday"
        : created.isSame(moment(), "week")
          ? "This Week"
          : created.isSame(moment().subtract(1, "week"), "week")
            ? "Last week"
            : created.isSame(moment(), "month")
              ? "This Month"
              : created.isSame(moment().subtract(1, "month"), "month")
                ? "Last Month"
                : created.format("MMMM YYYY");
    return { ...chatSession, period };
  });
  const chatsGroupedByPeriod = groupBy(chatsWithPeriod, "period");

  const renderChat = (chat: ChatNode) => {
    const isEditing = editingId === chat.id;
    const isActive = chat.id === activeChatSessionId;
    const dropdownOptions: DropdownOptionType[] = [
      {
        label: chat.pinnedAt ? "Unpin" : "Pin to top",
        onClick: () => handleTogglePin(chat),
      },
      {
        label: "Delete",
        onClick: () => handleDelete(chat.id),
        textColor: "text-red-500",
      },
    ];
    return (
      <div
        key={chat.id}
        className={classNames(
          "flex items-center justify-between group rounded-md px-2 py-2",
          isActive ? "bg-nav-item-active" : "hover:bg-nav-item-active",
        )}
      >
        {isEditing ? (
          <input
            autoFocus
            aria-label="Chat title"
            maxLength={255}
            className="flex-grow min-w-0 rounded border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:border-gray-400 focus:outline-none"
            value={draftTitle}
            placeholder={
              getChatSessionTitle(chat) || moment(chat.created).format("lll")
            }
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveRename(chat);
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelRename();
              }
            }}
            onBlur={() => saveRename(chat)}
          />
        ) : (
          <>
            <AppLink
              className={classNames("flex-grow min-w-0")}
              to={`/ask-ai/${chat.id}`}
              onClick={
                onClickChatSessionId
                  ? (e) => {
                      e.preventDefault();
                      onClickChatSessionId(chat.id);
                    }
                  : undefined
              }
            >
              <span className="block truncate text-gray-600 hover:text-gray-800 flex-grow">
                {getChatSessionTitle(chat) ||
                  moment(chat.created).format("lll")}
              </span>
              <span
                className={classNames(
                  "block truncate mt-px text-xs text-gray-400",
                )}
              >
                {chat.assessment?.target?.name
                  ? `${label("review", { titleCase: true })}: ${chat.assessment?.target?.name}`
                  : chat.meeting?.title
                    ? `${label("meeting", { titleCase: true })}: ${chat.meeting?.title}`
                    : "Homepage"}
              </span>
            </AppLink>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 shrink-0">
              <Button
                icon={getTopicflowIcon("edit")}
                theme={buttonTheme.iconGray}
                size={ButtonSize.small}
                aria-label="Rename chat"
                onClick={() => startRename(chat)}
              />
              <Dropdown
                size={ButtonSize.small}
                options={dropdownOptions}
                align="end"
                aria-label="Chat actions"
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className={classNames(
        "js-test-chat-history flex flex-col gap-4 text-sm",
        className,
      )}
    >
      <AppLink
        to="/ask-ai"
        onClick={(e) => {
          if (onClickChatSessionId) {
            e.preventDefault();
            onClickChatSessionId(null);
          }
        }}
        className="flex items-center gap-2 px-2 py-1 rounded text-sm text-gray-800 hover:bg-nav-item-active"
      >
        <NewChatIcon className="h-4 w-4 shrink-0 text-gray-500" />
        Start new chat
      </AppLink>
      {loading && !data ? (
        <Loading />
      ) : chatSessions.length === 0 ? (
        <div className="text-gray-400 px-2">No history</div>
      ) : (
        <>
          {pinnedChats.length > 0 && (
            <div>
              <div className="tf-header-sm mb-1 px-2">Pinned</div>
              {pinnedChats.map((chat) => renderChat(chat))}
            </div>
          )}
          {Object.entries(chatsGroupedByPeriod).map(([period, chats]) => (
            <div key={period}>
              <div className="tf-header-sm mb-1 px-2">{period}</div>
              {chats.map((chat) => renderChat(chat))}
            </div>
          ))}
        </>
      )}
      {data?.chatSessions?.pageInfo.hasNextPage && (
        <div className="flex items-center justify-center">
          <Button
            size={ButtonSize.medium}
            text="Load more"
            onClick={handleLoadMore}
          />
        </div>
      )}
    </div>
  );
};

export default ChatHistoryList;
